/**
 * "+ Publish" in the listing wizard — a real listing instead of a fabricated one.
 *
 * WHAT #wz-publish DID. app.js:4936 mints a listing client-side —
 * `LISTINGS.unshift({...})`, `MY_COLLECTION.unshift({...})` — and toasts
 * "Listed. It's live on the floor." with no server call. writeGuard deadened it,
 * so today the button does nothing at all. This wires it.
 *
 * ── THREE DECISIONS THAT SHAPE EVERYTHING BELOW ─────────────────────────────
 *
 * 🔒 1. IT STAYS IN GUARDED_WRITES, and that is a fail-safe rather than an
 * oversight. installWriteGuard uses `event.stopPropagation()`, never
 * stopImmediatePropagation (writeGuard.ts), on a `document` capture listener —
 * so a SECOND capture listener registered afterward still receives the click.
 * Both run. If anything in this module throws, the guard is still there and the
 * button stays dead instead of falling back to Blake's fabricator. The usual
 * "wire and unguard in the same commit" rule exists to prevent a window where
 * the fake write is live; here we get that window closed permanently instead.
 *
 * 🔴 2. WE DO NOT PUT THE NEW LISTING ON THE FLOOR, and this is the subtle one.
 * POST /api/listings always inserts `status: "under_review"` (listings.ts:1477)
 * and answers `201 { pendingReview: true, verifyJobId }`. Meanwhile
 * `__gbzMergeListing` is `LISTINGS.push(listing)` and `mapListingRow` carries NO
 * status field — DemoListing has none. An under-review row and an active row are
 * byte-identical to his renderer. Merging one would put it in browse, in the
 * category grid, in Discover, in "Similar", and it would LEAD New Arrivals,
 * because renderCategoryPage sorts that rail newest first and a just-published
 * row is the newest.
 * That moves the fabrication out of the toast and into the DOM, where it reads
 * as fact rather than as a claim. So: no merge.
 *
 * 🔴 3. WE DO NOT NAVIGATE ON SUCCESS. (Since Blake's ruling 2026-10-01 the
 * submitted sheet opens over the form on the 201 instead of a toast; it still
 * navigates nowhere until the seller picks one of its buttons. See publish.) His `render()` for a detail view emits a
 * FRESH EMPTY toast node, so any honest message we show is wiped within
 * milliseconds — and what replaces it is a page with vote bars, a price-check
 * panel, "escrow protected", and an armed offer composer. Nothing anywhere in
 * app.js renders an under-review state; the only "Under Review" literal in the
 * file belongs to a dispute. A true sentence that does not survive, followed by
 * a false page that does, is worse than saying nothing.
 *
 * ── THE COPY IS SHIPPED, NOT INVENTED ───────────────────────────────────────
 *
 * The success sentence is create-listing.tsx's own under-review branch, verbatim.
 * We deliberately do NOT copy its approval branch: it paints "Your item is now
 * live and visible in the feed" whenever the verify job returns
 * `decision === "approved"`, but promoteActive also requires `!moderationFailed`
 * and `!highValueHeld` and its CAS can refuse, while the job still reports
 * `status: "active"`. A high-value or moderation-flagged listing gets told it is
 * live while it sits in the admin queue. That is a live defect in the reference
 * and it is exactly the class this layer exists to remove — so this module does
 * not poll and does not claim approval. It reports the 201 and stops.
 */
import { mapListingRow, mapMyCollection, IN_REVIEW_STATUS, type ApiCollectionItem, type ApiListingRow, type DemoCollectionItem, type DemoListing, type DemoUser } from "./floorTransport";

/** His button (app.js:4936). */
export const PUBLISH_SELECTOR = "#wz-publish";

/** His step-1 "Next" (app.js:4930). */
export const NEXT_SELECTOR = "#wz-next";

/** The wizard step that collects category and subcategory. */
export const DETAILS_STEP = 1;

/**
 * create-listing.tsx's own under-review sentence, verbatim. Owner-approved
 * 2026-08-28 for reuse here rather than writing a new one.
 */
export const UNDER_REVIEW_MESSAGE =
  "Your listing has been submitted and is awaiting moderator approval. You'll be notified once it's live — usually within a few hours.";

/**
 * The sentence for a listing that really is live, and the poll that earns it.
 *
 * 🔴 THE ORIGINAL PREMISE WAS WRONG. This module used to say the under-review
 * sentence unconditionally, on the reading that `under_review` meant a manual
 * queue. The owner corrected it 2026-08-28: the AI image check runs
 * automatically and promotes a passing listing in about two to three seconds,
 * and the manual process predates it. So every publish was telling sellers
 * their real item was waiting on a human when it usually was not.
 *
 * POST /api/listings returns a `verifyJobId` (listings.ts:1307) which this
 * module was discarding. The job runs the scan and flips the row to `active`
 * (jobHandlers.ts:353) when it passes.
 */
export const LIVE_MESSAGE = "Listed. It's live on the floor.";

/**
 * Still scanning when the poll window closed. NOT a failure and NOT a queue:
 * the honest answer is that we do not know yet.
 */
export const STILL_CHECKING_MESSAGE =
  "Listed. We're still checking the photos — it'll appear on the floor shortly.";

/**
 * 📌 OWNER RULING PENDING on this wording. A listing over the $10,000
 * threshold fires a high-value flag (listings.ts:1179, lib/highValueFlag.ts)
 * and `promoteActive` refuses while it is open (jobHandlers.ts:315), so it
 * genuinely will not go live automatically. Telling that seller the generic
 * queue sentence leaves them thinking the scan failed.
 */
export const HIGH_VALUE_MESSAGE =
  "Listed. Items over $10,000 get a quick manual check before they hit the floor.";

/** The threshold that fires the high-value hold (lib/highValueFlag.ts). */
const HIGH_VALUE_CENTS_THRESHOLD = 10_000;

/**
 * Front-loaded, because the owner measured the scan at two to three seconds.
 *
 * create-listing.tsx polls a flat 1200ms twelve times, so the common case
 * waits out most of a cycle it has already earned. These delays resolve a 2.4s
 * scan on the sixth look and still give a slow one its full window, capping
 * around twelve seconds of real time across fourteen requests.
 */
export const POLL_DELAYS_MS: readonly number[] = [
  400, 400, 400, 400, 400, 400,
  800, 800, 800, 800,
  1200, 1200, 1200, 1200,
];

/** The server's own words for a request that arrived with no usable photo. */
export const NO_PHOTOS_MESSAGE =
  "At least one valid photo is required. Upload photos before publishing.";

/** Reused from the price-check interceptor, which faced the same 401/403. */
export const SIGNIN_MESSAGE = "Sign in to publish a listing";

/** Generic, and only for a failure with no server sentence to quote. */
export const GENERIC_FAILURE_MESSAGE = "Publish failed — please try again";

/** Upload accepts these only (api-server/src/routes/upload.ts). */
const UPLOADABLE_MIME = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

/** The one category the server exempts from requiring a subcategory. */
const SUBCATEGORY_EXEMPT = "other";

export interface WizardPublishDraft {
  title: string;
  description: string;
  cat: string | null;
  sub: string | null;
  attr: string | null;
  brand: string;
  year: string;
  condition: string | null;
  value: string;
  isAvailable: boolean;
  acceptsCash: boolean;
  acceptsItems: boolean;
  acceptsHybrid: boolean;
  grader: string | null;
  grade: string | null;
  cert: string;
  weightLb: number | null;
  weightOz: number | null;
  dimL: string;
  dimW: string;
  dimH: string;
  photos: string[];
  /** The item's own Trade for entries the seller left picked (PR B, card 1218637972579066 item 4). */
  tradeFor: string[];
  step: number;
  isEdit: boolean;
  /* THE EDIT CONTRACT (card 1217916966128774), from the bridge: the record this
     draft edits, its availability when the edit opened, and whether the seed
     from the server completed. See saveEdit. */
  editId: string | null;
  editCollectionId: string | null;
  editKind: "listing" | "collection" | null;
  editWasAvailable: boolean;
  editSeeded: boolean;
  /** The listing's status from the server's record; null for a collection
   *  piece, or a record without one. */
  editStatus: string | null;
  /** freshWiz's own number for this draft; null on a draft with none. */
  draftId: string | null;
  /** Whether the server's record named a brand (an emptied brand is a
   *  withdrawal only then). */
  editHadBrand: boolean;
  /** Whether the edit's read carried the Trade for column: a cleared list is withdrawn with null
   *  only then; otherwise the key is omitted and the server keeps what it has (PR B). */
  editHadTradeFor: boolean;
  /** Each stored photo's stored thumbnail, by the photo's url. */
  editThumbs: Record<string, string>;
}

export const SAVED_MESSAGE = "Changes saved.";
/** A listing under review cannot change availability until it is approved. */
export const SAVED_IN_REVIEW_MESSAGE = "Changes saved. This listing is still in review and goes live once approved.";
/** A collection piece saved with offers on: the route creates the listing under review. */
export const SAVED_LISTED_MESSAGE = "Changes saved. It's listed and awaiting moderator approval; it goes live once approved.";
/** The edit's own sign-in ask: a 401 on the update route. */
export const EDIT_SIGNIN_MESSAGE = "Sign in to save your changes";
export const EDIT_NOT_LOADED_MESSAGE = "This item's details haven't loaded yet. Try again in a moment.";
export const EDIT_SELLER_ONLY_MESSAGE = "Only the seller can edit this listing";
export const EDIT_FAILED_MESSAGE = "Could not save your changes. Try again.";
export const AVAILABILITY_FAILED_MESSAGE = "Your details were saved, but the listing's availability did not change. Try again.";
/** The same, with the server's own reason when it gave one. */
export const AVAILABILITY_FAILED_PREFIX = "Your details were saved, but the listing's availability did not change: ";
/** The switch asked for a change, but the listing's availability had moved on
 *  another device while the edit was open: a stale form never overrides that
 *  (the toggle route FLIPS whatever state it finds; off declines every pending
 *  offer). Copy pending Blake's word (adversarial pass 4, card 1219177682942591). */
export const AVAILABILITY_MOVED_MESSAGE =
  "Your details were saved. This listing's availability changed on another device while you were editing, so it was left as it is now.";

/** The COLLECTIONS route says the same thing one word differently. Quoting the
 *  wrong one is a small lie about which action just failed. */
export const NO_PHOTOS_SAVE_MESSAGE =
  "At least one valid photo is required. Upload photos before saving.";

interface PublishWindow extends Window {
  __gbzWizardPublishDraft?: () => WizardPublishDraft | null;
  /** The bridge's after-save hook: the page takes the server's row, the edit ends, the seller goes back. */
  __gbzApplyEdit?: (r: { listing?: DemoListing; item?: DemoCollectionItem; available?: boolean; inReview?: boolean; id?: string | null; accepts?: { acceptsCash: boolean; acceptsItems: boolean; acceptsHybrid: boolean } }) => boolean;
  /** The closet's merge: the collection branch's row, and since card
   *  1219177682942591 the listing branch's row too, in review. */
  __gbzMergeCollectionItem?: (item: Record<string, unknown>) => boolean;
  /** One closet row updated in place (the flip to ACTIVE). */
  __gbzUpdateCollectionItem?: (sel: { id?: string; lid?: string }, fields: Record<string, unknown>) => boolean;
  /** The page's rows take a listing that really went active (never one in review). */
  __gbzMergeListing?: (listing: DemoListing, user: DemoUser) => boolean;
  render?: () => void;
  rerenderKeepScroll?: () => void;
  toast?: (message: string) => void;
  /** The submitted sheet (app.js openPublishedSheet); true when it opened. */
  __gbzShowPublished?: (item: { title: string; value: number; thumb: string | null; draftId: string | null }) => boolean;
  /** The sheet's chip turns "Live" (app.js publishedSheetLive). */
  __gbzPublishedLive?: (draftId: string | null) => boolean;
}

/**
 * A picked photo is a data: URL (app.js reads files with FileReader), and the
 * uploader used to turn it back into bytes with `fetch(photo)`. That read is
 * governed by the page's Content Security Policy: connect-src applies to
 * fetch() of data: and blob: URLs too, and the policy that shipped on
 * 2026-09-16 lists neither, so the browser refused it and every device photo
 * failed to upload, on publish and on the scan (card 1218623355292077).
 * Decoding here keeps the read inside the page, so the policy can stay strict.
 * Returns null for anything that is not a data: URL.
 */
export function dataUrlToBlob(photo: string): Blob | null {
  const m = /^data:([^;,]*)((?:;[^;,]*)*),([\s\S]*)$/.exec(photo);
  if (!m) return null;
  const type = m[1] || "text/plain";
  const base64 = /;base64/i.test(m[2]);
  if (base64) {
    const bin = atob(m[3].replace(/\s+/g, ""));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type });
  }
  return new Blob([decodeURIComponent(m[3])], { type });
}

/** Three shapes live in wiz.photos, and only one of them may be uploaded. */
export type PhotoKind = "upload" | "passthrough" | "refuse";

export function classifyPhoto(photo: string): PhotoKind {
  if (photo.startsWith("data:")) return "upload";
  if (/^https?:\/\//i.test(photo)) return "passthrough";
  /* Anything else is one of HIS bundled asset paths.

     📌 The "Or start from a sample shot" chips that were the obvious way to
     get one into a draft left the wizard on 2026-09-13.

     ⚠️ AN EARLIER VERSION OF THIS COMMENT JUSTIFIED KEEPING THE BRANCH BY
     NAMING TWO LIVE ROUTES, AND NEITHER SURVIVES INSPECTION. The collection
     Edit / List-it path (app.js, `act === "edit" || act === "listit"`) writes
     `wiz.photo = m.image` — SINGULAR — and nothing anywhere reads `wiz.photo`;
     every reader, including the draft this module receives, uses `wiz.photos`.
     So that path carries no photo into the wizard at all, bundled or
     otherwise. That is a real pre-existing defect, carded separately; it is
     not something this comment should have been leaning on.

     THE BRANCH IS STILL KEPT, on the honest reason rather than the convenient
     one: it is the last line before a seller's listing is published carrying
     GoBazaar stock imagery as their own photograph. A classifier that refuses
     by SHAPE costs one string test and is correct for inputs nobody has
     thought of yet; deleting it because today's known entry points are closed
     or broken trades a permanent guarantee for a temporary fact. Note also
     that fixing the singular/plural defect above would immediately re-open a
     route that feeds it bundled paths.

     Uploading one would publish GoBazaar stock imagery as the seller's own
     photograph, which is fabrication by omission and the exact failure this
     layer exists to stop. */
  return "refuse";
}

/** The item's Trade for as the wire takes it: the entries picked, trimmed, each once. */
export function tradeForList(draft: WizardPublishDraft): string[] {
  const seen = new Set<string>();
  for (const label of draft.tradeFor ?? []) {
    const t = typeof label === "string" ? label.trim() : "";
    if (t) seen.add(t);
  }
  return [...seen];
}

/**
 * The payload, built from his draft.
 *
 * Exported so the mapping is testable without a network, because the mapping is
 * where the silent corruption would live: three of these fields are STRINGS in
 * his wizard and numbers on the wire, and one of them is a value/label pair
 * whose wrong half is a 400.
 */
export function buildListingPayload(
  draft: WizardPublishDraft,
  images: string[],
  renditionImages: string[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    title: draft.title.trim(),
    description: draft.description.trim(),
    category: draft.cat,
    subcategory: draft.sub ?? undefined,
    condition: draft.condition,
    declaredValue: Number(draft.value),
    acceptsCash: draft.acceptsCash,
    acceptsItems: draft.acceptsItems,
    acceptsHybrid: draft.acceptsHybrid,
    exchangeType: exchangeTypeFor(draft),
    images,
    renditionImages,
  };

  /* 🔴 condition is the VALUE half of his [value, label] pair. His own
     fabricator looks the LABEL up before storing it (app.js:4944) — copying
     that lookup into the payload sends "Good" where the enum wants "good", and
     the server answers 400. */

  const brand = draft.brand.trim();
  if (brand) payload.brand = brand; // the reference implementation drops this
  if (draft.attr) payload.itemSize = draft.attr;

  const year = Number(draft.year);
  if (draft.year.trim() && Number.isFinite(year)) payload.acquiredYear = year;

  // Both halves are strings off his inputs; ounces are a fraction of a pound.
  const lb = Number(draft.weightLb) || 0;
  const oz = Number(draft.weightOz) || 0;
  if (lb > 0 || oz > 0) payload.packageWeightLb = lb + oz / 16;

  const dims: Array<[string, string]> = [
    ["packageLengthIn", draft.dimL],
    ["packageWidthIn", draft.dimW],
    ["packageHeightIn", draft.dimH],
  ];
  for (const [key, raw] of dims) {
    const n = Number(raw);
    if (raw.trim() && Number.isFinite(n) && n > 0) payload[key] = n;
  }

  const tradeFor = tradeForList(draft);
  if (tradeFor.length) payload.exchangeFor = tradeFor.join(",");

  /* 🪤 graded is a TRI-STATE, not a boolean. "Raw" is an explicit answer and
     must ride as graded:false; an untouched grader is "never asked" and must
     omit all four keys. Truthiness-filtering collapses the first into the
     second and loses what the seller actually said. */
  if (draft.grader === "Raw") {
    payload.graded = false;
  } else if (draft.grader) {
    payload.graded = true;
    payload.grader = draft.grader;
    if (draft.grade) payload.grade = draft.grade;
    if (draft.cert.trim()) payload.certNumber = draft.cert.trim();
  }

  return payload;
}

/**
 * The collection-only payload.
 *
 * Deliberately NOT the listing payload with a flag flipped: this route has its
 * own field names and its own required set. `estimatedValue` rather than
 * `declaredValue`, an `imageUrl` COVER SCALAR alongside the optional `images[]`,
 * and `title` plus one photo are the only things it insists on.
 *
 * `isAvailable: false` is the whole point — it is what makes the server create
 * a collection row and NOT a listing. Sending true here would publish to the
 * floor from the branch whose entire meaning is "not for sale".
 */
export function buildCollectionPayload(
  draft: WizardPublishDraft,
  images: string[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    title: draft.title.trim(),
    description: draft.description.trim(),
    imageUrl: images[0],
    images,
    estimatedValue: Number(draft.value) || 0,
    category: draft.cat,
    condition: draft.condition,
    isAvailable: false,
  };
  if (draft.sub) payload.subcategory = draft.sub;
  if (draft.attr) payload.itemSize = draft.attr;
  const year = Number(draft.year);
  if (draft.year.trim() && Number.isFinite(year)) payload.acquiredYear = year;
  return payload;
}

/**
 * The LISTING edit body: the create payload's mapping (it is the one place the
 * wizard's strings become the route's numbers and enums), plus the two things
 * an edit can say that a create cannot: a brand the seller emptied is
 * withdrawn, and a year the seller emptied is cleared (`acquiredYear: null`;
 * the route clears on an explicit null and keeps on an absent key).
 *
 * The brand goes the route's own way (listingBrandAnswer.test.ts): a
 * withdrawal is the PAIR `brand: null, noBrand: true`, and only when the
 * server's record named a brand to withdraw. A blank left blank sends
 * neither key, because `noBrand: true` records "the seller said there is no
 * brand", which nobody said; and `noBrand: true` without `brand: null` on a
 * row that names one trips the table's own check (a 500, nothing saved).
 *
 * PATCH /api/listings/:id accepts every key here (updateListingBodySchema);
 * `status` is never sent, because the route refuses it and availability has
 * its own route.
 */
export function buildListingEditPayload(
  draft: WizardPublishDraft,
  images: string[],
  renditionImages: string[],
): Record<string, unknown> {
  const payload = buildListingPayload(draft, images, renditionImages);
  if (!draft.brand.trim() && draft.editHadBrand) {
    payload.brand = null;
    payload.noBrand = true;
  }
  if (!draft.year.trim()) payload.acquiredYear = null;
  /* A size or attribute cleared in the wizard is withdrawn too (the create
     mapping omits an empty one, and the route keeps an absent key). */
  if (!draft.attr) payload.itemSize = null;
  /* The item's Trade for cleared in the wizard is withdrawn too: null is what the create path
     stores for none (listings.ts:1392). Omitting the key kept the old value (card 1218637972579066,
     item 4). */
  if (draft.editHadTradeFor && !tradeForList(draft).length) payload.exchangeFor = null;
  /* Grading can be withdrawn too: Raw clears the grader, the grade and the
     cert (the create mapping sends graded false alone, which leaves the old
     slab's fields on the row), and a graded card with its cert emptied
     clears the cert. The columns are nullable and the route writes nulls. */
  if (draft.grader === "Raw") {
    payload.grader = null;
    payload.grade = null;
    payload.certNumber = null;
  } else if (draft.grader && !draft.cert.trim()) {
    payload.certNumber = null;
  }
  return payload;
}

/**
 * The COLLECTION edit body, for a collection-only item (no linked listing):
 * the create payload's names, plus the fields an edit carries that a create
 * from the wizard never did (condition and year already, and now the three
 * exchange booleans, the grading and the packaging), and `isAvailable` as the
 * seller left the switch. With it true the route creates the linked listing
 * itself, and asks for the packaging first (collections.ts's own guard), so
 * the packaging rides when it is filled in.
 */
export function buildCollectionEditPayload(draft: WizardPublishDraft, images: string[]): Record<string, unknown> {
  const payload = buildCollectionPayload(draft, images);
  payload.isAvailable = draft.isAvailable;
  /* An edit can withdraw what a create only adds: a cleared year or size
     (the route takes null for both, collections.ts), and it carries the item's
     Trade for, which the wizard shows once the piece is open to offers and the
     route accepts as exchangeFor. */
  if (!draft.year.trim()) payload.acquiredYear = null;
  if (!draft.attr) payload.itemSize = null;
  const tradeFor = tradeForList(draft);
  if (tradeFor.length) payload.exchangeFor = tradeFor.join(",");
  else if (draft.editHadTradeFor) payload.exchangeFor = null;
  payload.acceptsCash = draft.acceptsCash;
  payload.acceptsItems = draft.acceptsItems;
  payload.acceptsHybrid = draft.acceptsHybrid;
  payload.exchangeType = exchangeTypeFor(draft);
  if (draft.grader === "Raw") {
    payload.graded = false;
  } else if (draft.grader) {
    payload.graded = true;
    payload.grader = draft.grader;
    if (draft.grade) payload.grade = draft.grade;
    if (draft.cert.trim()) payload.certNumber = draft.cert.trim();
  }
  const lb = Number(draft.weightLb) || 0;
  const oz = Number(draft.weightOz) || 0;
  if (lb > 0 || oz > 0) payload.packageWeightLb = lb + oz / 16;
  const dims: Array<[string, string]> = [
    ["packageLengthIn", draft.dimL],
    ["packageWidthIn", draft.dimW],
    ["packageHeightIn", draft.dimH],
  ];
  for (const [key, raw] of dims) {
    const n = Number(raw);
    if (raw.trim() && Number.isFinite(n) && n > 0) payload[key] = n;
  }
  return payload;
}

/**
 * 🔴 THIS SHIPPED WRONG AND 400'd EVERY PUBLISH BUT ONE (found on staging
 * 2026-08-28). The previous version returned "both" and "sale" — Blake's CHIP
 * vocabulary from the wizard's own review step — against a server enum of
 * ["trade","cash","hybrid","free"]. Three of its four branches sent a value
 * that does not exist, so anything except items-only was refused with
 * `Invalid option: expected one of "trade"|"cash"|"hybrid"|"free"`. Its
 * docblock claimed it was create-listing.tsx's derivation "kept identical".
 * It was not, and nothing asserted it — there was no test on this field at all.
 *
 * 🔑 MIRRORS THE SERVER, NOT create-listing.tsx, AND THAT IS DELIBERATE.
 * The obvious repair was to copy the reference page. Its derivation
 * (create-listing.tsx:232) disagrees with the server on one combination:
 * cash AND items AND NOT hybrid, where it sends "hybrid" and the server stores
 * "trade". That is invisible today only because POST /api/listings recomputes
 * the enum from the three booleans whenever any of them is present
 * (listings.ts:1083 boolsProvided, :1103) and ignores whatever the client sent.
 * So the field is VALIDATED but never USED. Copying the reference would have
 * shipped a value that disagrees with the row the server actually writes;
 * mirroring deriveEnumFromBooleans (listings.ts:70-78) cannot.
 *
 * Why send it at all, given the server ignores it: create-listing.tsx sends it,
 * both web publish paths should look the same on the wire, and it is the
 * fallback the server uses if the booleans are ever absent. It must simply
 * always equal what the server would compute — which the test asserts across
 * all eight boolean combinations rather than trusting this comment.
 */
function exchangeTypeFor(draft: WizardPublishDraft): "trade" | "cash" | "hybrid" {
  if (draft.acceptsHybrid) return "hybrid";
  if (draft.acceptsCash && !draft.acceptsItems) return "cash";
  return "trade";
}

export function installWizardPublish(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as PublishWindow).toast?.(message);
    },
    newKey = () => win.crypto.randomUUID(),
  }: { notify?: (m: string) => void; newKey?: () => string } = {},
): () => void {
  /* In flight, or already published this draft. His button carries no
     `disabled` and his handler no re-entrancy guard — today its only safety is
     that the fabricator navigates away synchronously. The moment a real await
     sits in here, the button is live for the whole round trip, and two clicks
     are two listings, two verify jobs and two listingCount increments.

     🪤 AND `Idempotency-Key` IS NOT THE SERVER-SIDE BACKSTOP IT LOOKS LIKE.
     All three POSTs below send one, but only POST /api/listings actually runs
     idempotencyMiddleware (listings.ts:974). On POST /api/collections
     (collections.ts:1369) and POST /api/upload the header is inert: nothing
     reads it, and a replayed request mints a second row or a second storage
     object. So on the collection branch these two flags are the WHOLE
     protection, not a client convenience sitting on top of a server
     guarantee.

     The header is still sent on all three — it costs nothing, it is already
     correct if either route gains the middleware, and sending it on only one
     would leave the create's real one reading like an oddity instead of the
     rule. What must not happen is someone seeing three identical headers,
     inferring protection that exists on one of them, and relaxing these
     flags on that basis. */
  let busy = false;
  /** Which press owns the busy latch (see the click handler). */
  let pressSeq = 0;
  let published = false;
  /* Which DRAFT was published: the latch is per draft, so one create does not
     block the next draft in the same page session, and an edit is never
     latched at all (round two: after one create, no edit could save). A draft
     with no number falls back to the per-session latch. */
  let publishedDraftId: string | null = null;

  /* Seam. Tests drive the poll without spending twelve real seconds. */
  const sleep = (ms: number) => new Promise<void>((r) => win.setTimeout(r, ms));

  /* Uploaded objects, keyed by the source photo string, so a retry after a
     failed create does not re-upload and orphan storage objects. */
  const uploaded = new Map<string, { url: string; renditionUrl: string }>();
  let idempotencyKey = newKey();

  async function uploadOne(photo: string): Promise<{ url: string; renditionUrl: string } | null> {
    const cached = uploaded.get(photo);
    if (cached) return cached;

    /* Decoded in place, never fetched: see dataUrlToBlob. The fetch fallback
       is for a stored https object handed straight to this function, which
       classifyPhoto normally routes around as a passthrough. */
    const blob = dataUrlToBlob(photo) ?? (await (await fetch(photo)).blob());
    /* Pre-checked, because upload.ts's fileFilter rejects with a bare Error
       carrying no statusCode and that route has no error handler — so a HEIC
       pick comes back as a 500 "Something went wrong. Please try again." and
       is indistinguishable from an outage, while advising a retry that fails
       identically every time. */
    if (!UPLOADABLE_MIME.has(blob.type)) return null;

    const form = new FormData();
    // Explicit type: a typeless Blob posts as application/octet-stream and is
    // rejected. Never set Content-Type on the request — it strips the boundary.
    form.append("file", new File([blob], "photo.jpg", { type: blob.type }));
    const res = await authFetch("/api/upload", {
      method: "POST",
      body: form,
      headers: { "Idempotency-Key": `${idempotencyKey}-${uploaded.size}` },
    });
    if (!res.ok) return null;
    const { url } = (await res.json()) as { url: string };

    /* The rendition is non-fatal and falls back to the master, matching the
       reference. renditionImages is INDEX-ALIGNED with images, so dropping a
       failure instead of substituting would shift every later thumbnail onto
       the wrong photo. */
    const out = { url, renditionUrl: url };
    uploaded.set(photo, out);
    return out;
  }

  /**
   * Lend the uploader — and, more to the point, its CACHE — to the cover-photo
   * scan (wizardScan.ts).
   *
   * The scan has to upload the cover before it can be read, and the card's rule
   * is that the same photo is never uploaded twice: the URL the scan obtains
   * must be the one publish sends. `uploaded` is keyed by the source photo
   * string, so a scan that ran earlier leaves publish's own `uploadOne` with a
   * cache hit and no second round-trip.
   *
   * 🔑 PUBLISHED RATHER THAN EXTRACTED, deliberately. Lifting uploadOne into a
   * shared module would have moved the idempotency-key handling that sits
   * around it on the listing-creation path, which is not a thing to restructure
   * for a convenience that one line buys. Nothing about publish's behaviour
   * changes here — this only makes an existing function reachable.
   */
  (win as PublishWindow & { __gbzUploadWizardPhoto?: typeof uploadOne }).__gbzUploadWizardPhoto =
    uploadOne;

  /**
   * The collection-only half of his button (wiz.isAvailable === false).
   *
   * 🔑 THIS BRANCH MAY DO WHAT THE LISTING BRANCH MUST NOT. POST
   * /api/collections with isAvailable:false creates the row outright — no
   * verify job, no under_review, a plain 201 — and MY_COLLECTION is the
   * SELLER'S OWN closet, not a buyer-facing surface. So the row really exists
   * by the time we show it, and showing it is the honest thing rather than a
   * claim about a pending state.
   *
   * That is why this one merges and navigates while the listing branch does
   * neither. The asymmetry is the point, not an inconsistency.
   */
  async function saveToCollection(draft: WizardPublishDraft, images: string[]): Promise<void> {
    const res = await authFetch("/api/collections", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(buildCollectionPayload(draft, images)),
    });

    if (res.status === 401 || res.status === 403) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      notify(body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    published = true;
    publishedDraftId = draft.draftId;
    idempotencyKey = newKey();

    /* Merge the row the server just confirmed, then let his own flow continue:
       he navigates to #/profile so the visitor lands on the closet holding it.
       We do not toast — his branch never did, and the row appearing IS the
       feedback, which is now true. */
    const row = (await res.json().catch(() => null)) as { id?: string } | null;
    const merge = (win as PublishWindow).__gbzMergeCollectionItem;
    if (row?.id && typeof merge === "function") {
      merge({
        id: row.id,
        title: draft.title.trim(),
        image: images[0],
        value: Number(draft.value) || 0,
        // collectionStatusFor(null) returns exactly this, so the literal his
        // own handler uses is already the one the server would report.
        status: "IN COLLECTION",
        chips: [],
        note: "Not listed · collection only",
      });
    }
    (win as PublishWindow).location.hash = "#/profile";
    (win as PublishWindow).render?.();
  }

  /**
   * Wait for the image check, then report what is ACTUALLY true.
   *
   * 🔑 READS THE LISTING'S STATUS, NOT THE JOB'S VERDICT, and that is the whole
   * reason this is not a copy of create-listing.tsx. That page paints "live in
   * the feed" on `job.result.decision === "approved"` alone. The server needs
   * more than the decision: promoteActive is
   *   decision === "approved" && !moderationFailed && !highValueHeld
   * (jobHandlers.ts:315) and its compare-and-swap can still refuse. A $12,000
   * guitar therefore gets told it is live while it sits in the admin queue.
   * One extra GET closes that: the row is the only thing that knows.
   *
   * Every arm returns a sentence that is true when it is said, including the
   * one that admits we ran out of patience rather than out of information.
   */
  async function settle(
    listingId: string | undefined,
    verifyJobId: string | null,
    draft: WizardPublishDraft,
  ): Promise<string> {
    /* No job id means no scan was queued, so there is nothing to wait for and
       nothing to claim. The row is under_review and stays there. */
    if (!verifyJobId || !listingId) return UNDER_REVIEW_MESSAGE;

    for (const delay of POLL_DELAYS_MS) {
      await sleep(delay);
      let job: { status?: string } | null = null;
      try {
        const jr = await authFetch(`/api/jobs/${encodeURIComponent(verifyJobId)}`);
        if (jr.ok) job = (await jr.json()) as { status?: string };
      } catch {
        /* A dropped poll is not an answer. Keep looking until the window
           closes, then fall through to the honest "still checking". */
      }
      if (job?.status !== "done" && job?.status !== "failed") continue;

      /* The job finished. Ask the ROW, not the job. */
      const row = await readListing(listingId);
      if (statusOf(row) === "active") {
        goLive(row as ApiListingRow, draft);
        return LIVE_MESSAGE;
      }
      /* It did not promote. The threshold is the one reason a seller can act
         on, and it is knowable from their own draft without another call. */
      if (Number(draft.value) > HIGH_VALUE_CENTS_THRESHOLD) return HIGH_VALUE_MESSAGE;
      return UNDER_REVIEW_MESSAGE;
    }

    /* The window closed with the scan still running. Neither outcome has
       happened yet, so neither is claimed. */
    return STILL_CHECKING_MESSAGE;
  }

  /** The row, as GET /api/listings/:id sends it: the ENVELOPE { listing, seller }
   *  (listings.ts:1192), which serves every status but `removed` (listings.ts
   *  :1131), so an under_review row reads back fine. null when it could not be
   *  read, or when the body is not that shape.
   *
   *  🔴 THIS USED TO READ `row.status` ON THE ENVELOPE, where there is no such
   *  field, so the status was always null, the poll never saw "active", and the
   *  sheet's chip never turned Live on prod (card 1219177682942591). The tests
   *  answered a flat { status } the server never sent, and passed. The status
   *  lives at `row.listing.status`, and only there. */
  async function readListing(listingId: string): Promise<ApiListingRow | null> {
    try {
      const r = await authFetch(`/api/listings/${encodeURIComponent(listingId)}`);
      if (!r.ok) return null;
      const row = (await r.json()) as Partial<ApiListingRow> | null;
      return row && row.listing && typeof row.listing === "object" ? (row as ApiListingRow) : null;
    } catch {
      return null;
    }
  }
  const statusOf = (row: ApiListingRow | null): string | null =>
    row ? ((row.listing as { status?: string | null }).status ?? null) : null;

  /** The tile's exchange chips from the three booleans, the closet's own vocabulary. */
  const termChips = (cash: boolean, items: boolean, hybrid: boolean): string[] =>
    [cash && "SALE", items && "TRADE", hybrid && "HYBRID"].filter((c): c is string => typeof c === "string");

  /**
   * THE FLIP (card 1219177682942591). The row went active: the closet tile the
   * 201 put up turns ACTIVE with the terms it carries, the listing joins the
   * page's rows so View Listing, Live Listings and the profile read it, and a
   * seller standing on Your Collection (#/profile, or #/u/<their handle>,
   * which paints the same view) sees it without a reload. Still off the
   * floor's order (__gbzOnFloor promotes installed ids only), so it never
   * leads New Arrivals from here.
   *
   * The repaint is held while the seller is typing, while any text box on the
   * page holds text (an open Quick Edit pop-up does), or while a drag is in
   * progress: a repaint would wipe all three, and the next paint shows the
   * flip. A toast that is showing is shown again after it, as
   * dealsReadState's own repaint does. Any other page is left alone.
   *
   * Every step is guarded: a bridge an older bundle lacks, or a row the mapper
   * refuses, must not turn a true "live" sentence into a failure.
   */
  function goLive(row: ApiListingRow, draft: WizardPublishDraft): void {
    const page = win as PublishWindow;
    const l = row.listing;
    try {
      const desc = (l.description ?? draft.description ?? "").trim();
      page.__gbzUpdateCollectionItem?.({ lid: l.id }, {
        status: "ACTIVE",
        chips: termChips(l.acceptsCash, l.acceptsItems, l.acceptsHybrid),
        note: desc || null,
      });
    } catch {
      /* the tile stays in review until the next load; the sentence is still true */
    }
    try {
      const mapped = mapListingRow(row);
      page.__gbzMergeListing?.(mapped.listing, mapped.user);
    } catch {
      /* the page does not hold the row until the next load */
    }
    const doc = page.document;
    const closetPainted = !!doc.querySelector("#closet-add"); // the owner's add tile: only Your Collection paints it
    const active = doc.activeElement as HTMLElement | null;
    const typing = !!active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
    const holdsText = [...doc.querySelectorAll("#app input, #app textarea")].some((el) => (el as HTMLInputElement).value);
    const dragging = !!doc.querySelector(".mgr-grid.reordering");
    if (closetPainted && !typing && !holdsText && !dragging) {
      try {
        const showing = doc.querySelector("#toast.show")?.textContent;
        page.rerenderKeepScroll?.();
        if (showing) page.toast?.(showing);
      } catch {
        /* the next repaint shows it */
      }
    }
  }

  /** The photo strip, uploaded where it is new and passed through where it is
   *  already stored; shared by a create and an edit. Empty means nothing
   *  usable was there. */
  async function collectPhotos(draft: WizardPublishDraft): Promise<{ images: string[]; renditionImages: string[] }> {
    const images: string[] = [];
    const renditionImages: string[] = [];
    for (const photo of draft.photos) {
      const kind = classifyPhoto(photo);
      if (kind === "refuse") continue;
      if (kind === "passthrough") {
        // Already a stored object on an allowlisted host. Re-uploading it would
        // mint a duplicate and a second moderation job.
        images.push(photo);
        renditionImages.push((draft.editThumbs && draft.editThumbs[photo]) || photo);
        continue;
      }
      const up = await uploadOne(photo);
      if (!up) continue;
      images.push(up.url);
      renditionImages.push(up.renditionUrl);
    }
    return { images, renditionImages };
  }

  async function failureMessage(res: Response, fallback: string): Promise<string> {
    const body = (await res.json().catch(() => ({}))) as { error?: string; errors?: Array<{ message?: string }> };
    return body.errors?.[0]?.message ?? body.error ?? fallback;
  }

  /**
   * THE EDIT (card 1217916966128774). The wizard opened as an edit of one
   * record (editKind, editId or editCollectionId, from the bridge); this saves
   * it through the routes that UPDATE rather than create: PATCH
   * /api/listings/:id for a listed item, then PATCH .../toggle-availability
   * when the switch moved; PATCH /api/collections/:id for a collection-only
   * item, which lists it itself when the switch is on. Then the page takes the
   * server's row back through the bridge and the seller returns to where they
   * pressed Edit. A draft the install layer never finished seeding is refused:
   * saving it would write the new-listing defaults over the seller's own
   * toggles, packaging and grading, the exact loss this exists to end.
   */
  async function saveEdit(draft: WizardPublishDraft): Promise<void> {
    if (!draft.editSeeded) {
      notify(EDIT_NOT_LOADED_MESSAGE);
      return;
    }
    if (draft.isAvailable && draft.cat !== SUBCATEGORY_EXEMPT && !draft.sub?.trim()) {
      notify(`subcategory is required for category '${draft.cat ?? ""}'`);
      return;
    }
    const { images, renditionImages } = await collectPhotos(draft);
    if (images.length === 0) {
      notify(draft.isAvailable ? NO_PHOTOS_MESSAGE : NO_PHOTOS_SAVE_MESSAGE);
      return;
    }
    const apply = (win as PublishWindow).__gbzApplyEdit;

    if (draft.editKind === "listing" && draft.editId) {
      const id = encodeURIComponent(draft.editId);
      const res = await authFetch(`/api/listings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildListingEditPayload(draft, images, renditionImages)),
      });
      if (res.status === 401) { notify(EDIT_SIGNIN_MESSAGE); return; }
      // The server's own sentence first (a listing in a trade says so); ours only without one.
      if (res.status === 403) { notify(await failureMessage(res, EDIT_SELLER_ONLY_MESSAGE)); return; }
      if (!res.ok) { notify(await failureMessage(res, EDIT_FAILED_MESSAGE)); return; }

      /* The page's row takes the SERVER's answer, not the draft: what it
         stored is the truth the seller will see everywhere else. Read right
         after the save, BEFORE availability is decided, because the save
         itself can move a listing into review (a value crossing $10,000). */
      let listing: DemoListing | undefined;
      let freshStatus: string | null = null;
      try {
        const fresh = await authFetch(`/api/listings/${id}`);
        if (fresh.ok) {
          const row = (await fresh.json()) as ApiListingRow;
          listing = mapListingRow(row)?.listing;
          freshStatus = (row.listing as { status?: string | null }).status ?? null;
        }
      } catch {
        /* The save landed; a failed re-read only means the page repaints from
           what it already had until the next load. */
      }

      /* Availability follows the record's status, the fresh one when the
         re-read gave it. Only active, unlisted and paused can be toggled (the
         route refuses the rest); under review is saved as it is and said so;
         a record without a status (an older server) falls back to what the
         door knew. */
      const status = freshStatus ?? draft.editStatus;
      const canToggle = !status || status === "active" || status === "unlisted" || status === "paused";
      let available: boolean | undefined =
        status === "active" ? true
        : status === "unlisted" || status === "paused" ? false
        : status ? undefined
        : draft.editWasAvailable;
      let availabilityFailure: string | null = null;
      /* The toggle route FLIPS whatever state the listing is in (off declines
         every pending offer), so it is sent only when the switch asks for a
         change AND nothing moved the availability elsewhere while the edit was
         open. Cases this closes (adversarial passes 3 to 5, card
         1219177682942591): a row in review whose photo check approved it
         mid-edit (the old compare sent the toggle and UNLISTED the
         just-approved listing); a listing hidden or re-listed from the phone
         mid-edit (a stale form must not undo that, with or without a warning on
         screen); and a row in review approved and then hidden on the phone
         mid-edit (the door's switch is the server's LOCK, held on, not the
         listing's state: measured against the lock, a hide elsewhere is a move).
         When the re-read gave no status, the door's value is all there is, and
         `available` is then the door's state too (above), so nothing can read
         as moved. canToggle already means `available` is a boolean. */
      if (canToggle && typeof available === "boolean") {
        const doorState = draft.editStatus === "under_review" ? true : draft.editWasAvailable;
        const movedElsewhere = available !== doorState;
        /* The switch is the seller's wish; it is unmet when it differs from the state the listing is in now. */
        const wantsChange = draft.isAvailable !== available;
        if (movedElsewhere && wantsChange) {
          // The wish is unmet because the listing moved elsewhere: say so, change nothing.
          availabilityFailure = AVAILABILITY_MOVED_MESSAGE;
        }
        if (!movedElsewhere && wantsChange) {
          const t = await authFetch(`/api/listings/${id}/toggle-availability`, { method: "PATCH" });
          if (t.ok) {
            /* The route answers with the status it flipped to; the tile wears that, not the wish. */
            const flipped = (await t.json().catch(() => ({}))) as { status?: string | null };
            available = typeof flipped.status === "string" ? flipped.status === "active" : draft.isAvailable;
          } else if (t.status === 401) {
            availabilityFailure = AVAILABILITY_FAILED_MESSAGE;
          } else {
            const words = await failureMessage(t, "");
            availabilityFailure = words ? AVAILABILITY_FAILED_PREFIX + words : AVAILABILITY_FAILED_MESSAGE;
          }
        }
      }
      apply?.({
        listing,
        available,
        // The save put the listing in review (the high-value hold), or it still is: the tile says so.
        inReview: status === "under_review",
        id: draft.editId,
        // The tile's chips when the re-read failed and the page never held the row.
        accepts: { acceptsCash: draft.acceptsCash, acceptsItems: draft.acceptsItems, acceptsHybrid: draft.acceptsHybrid },
      });
      // A refused availability change is the last word, not covered by "saved".
      notify(availabilityFailure ?? (status === "under_review" ? SAVED_IN_REVIEW_MESSAGE : SAVED_MESSAGE));
      return;
    }

    if (draft.editKind === "collection" && draft.editCollectionId) {
      const cid = encodeURIComponent(draft.editCollectionId);
      const res = await authFetch(`/api/collections/${cid}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildCollectionEditPayload(draft, images)),
      });
      if (res.status === 401) { notify(EDIT_SIGNIN_MESSAGE); return; }
      if (res.status === 403) { notify(await failureMessage(res, EDIT_SELLER_ONLY_MESSAGE)); return; }
      if (!res.ok) { notify(await failureMessage(res, EDIT_FAILED_MESSAGE)); return; }

      let item: DemoCollectionItem | undefined;
      try {
        const fresh = await authFetch("/api/collections");
        if (fresh.ok) {
          const rows = (await fresh.json()) as ApiCollectionItem[];
          const mine = Array.isArray(rows) ? rows.filter((r) => r && r.id === draft.editCollectionId) : [];
          item = mapMyCollection(mine)[0];
        }
      } catch {
        /* As above: saved, re-read later. */
      }
      apply?.({ item });
      // With offers on, the route created the listing under review: say so.
      notify(draft.isAvailable ? SAVED_LISTED_MESSAGE : SAVED_MESSAGE);
      return;
    }

    notify(EDIT_FAILED_MESSAGE);
  }

  async function publish(draft: WizardPublishDraft): Promise<void> {
    /* 🔴 THE EDIT FLOW SHARES THIS ROUTE. "Edit listing" and the collection's
       Edit call freshWiz(), prefill from a REAL record and send the seller to
       #/list, so by the time the draft reaches this button it looks like a
       create. Publishing it would mint a DUPLICATE. `isEdit` is the only thing
       in the runtime that knows, and an edit goes to saveEdit: the update
       routes, never the create. */
    if (draft.isEdit) {
      await saveEdit(draft);
      return;
    }

    /* The subcategory rule belongs to the LISTING route only. POST
       /api/collections destructures `subcategory` and requires nothing of it,
       so gating the collection branch on it would refuse a save the server
       would have accepted. */
    if (draft.isAvailable && draft.cat !== SUBCATEGORY_EXEMPT && !draft.sub?.trim()) {
      // The server's own rule, refused before a byte is uploaded.
      notify(`subcategory is required for category '${draft.cat ?? ""}'`);
      return;
    }

    const { images, renditionImages } = await collectPhotos(draft);

    if (images.length === 0) {
      /* Each route has its OWN sentence for this and they differ by one word —
         "publishing" vs "saving". Quoting the wrong one is a small lie about
         which thing just failed. */
      notify(draft.isAvailable ? NO_PHOTOS_MESSAGE : NO_PHOTOS_SAVE_MESSAGE);
      return;
    }

    if (!draft.isAvailable) {
      await saveToCollection(draft, images);
      return;
    }

    const res = await authFetch("/api/listings", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(buildListingPayload(draft, images, renditionImages)),
    });

    if (res.status === 401 || res.status === 403) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        errors?: Array<{ message?: string }>;
      };
      // The server's words when it gave any, ours only when it did not.
      notify(body.errors?.[0]?.message ?? body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    /* 201. The row exists. Whether it is LIVE is a separate question, and the
       next few seconds answer it. Marked published either way so a second
       press cannot re-post while the scan runs. */
    published = true;
    publishedDraftId = draft.draftId;
    idempotencyKey = newKey();
    /* The button is free again from here: the per-draft latch above is what
       stops a second create of THIS draft, and the poll below can take twelve
       seconds, during which the seller may open the new tile's Edit and press
       Save (an edit, through saveEdit); held busy, that press did nothing and
       said nothing (adversarial pass 3, card 1219177682942591). */
    busy = false;

    const created = (await res.json().catch(() => ({}))) as {
      id?: string;
      verifyJobId?: string | null;
    };

    /* THE CLOSET (card 1219177682942591). The row exists, so the seller's own
       Your Collection shows it at once: the first tile, IN REVIEW, the listed
       price, the terms, under the All tab only (renderProfile counts ACTIVE
       alone as live). MY_COLLECTION is the seller's own view, which is why
       this is honest where a merge onto the floor would not be (rule 2 above
       still stands: nothing under review reaches LISTINGS). The row's id is
       the server's own placeholder form, "listing:" plus the listing id: the
       publish created the real collection row (listings.ts
       ensureCollectionItemForListing), the next load carries that row's own
       id, and until then reorder resolves the placeholder to it
       (collections.ts PATCH /reorder). A bridge an older bundle lacks leaves
       the tile to the next load. */
    if (created.id) {
      try {
        (win as PublishWindow).__gbzMergeCollectionItem?.({
          id: `listing:${created.id}`,
          lid: created.id,
          title: draft.title.trim(),
          image: images[0],
          value: Number(draft.value) || 0,
          status: IN_REVIEW_STATUS,
          chips: termChips(draft.acceptsCash, draft.acceptsItems, draft.acceptsHybrid),
          // The description, as every closet row carries it (the tile draws its own
          // "Not visible to buyers yet" line while in review; the Exchange deck reads the note as the item's text).
          ...(draft.description.trim() ? { note: draft.description.trim() } : {}),
        });
      } catch {
        /* the tile waits for the next load */
      }
    }

    /* THE SUBMITTED SHEET (walk #12, Blake's ruling 2026-10-01, which retires
       rule 3's "sit on the filled form" for this one moment). The 201 is in
       hand, so the sheet opens now, over the form, saying "Under review"; it
       does not wait for the image check. It repaints the page, and the toast
       it would have wiped is not needed: the sheet says the same thing.
       Nothing is merged onto the floor and nothing navigates (rule 2 stands;
       the seller's own closet took the row above): the seller leaves through
       the sheet's own buttons. The bridge answers false when
       the page could not take it, and the sentence is said instead.

       🪤 A THROW from the bridge is the same as a false. The row exists (the
       201 is in hand, the latch above is set), so letting a render exception
       reach the click handler's catch would say "Publish failed — please try
       again" about a listing that was published, and the latch would then
       refuse the retry the sentence asked for. Caught here, it falls to the
       sentence path, which is true. */
    let sheet = false;
    try {
      sheet = (win as PublishWindow).__gbzShowPublished?.({
        title: draft.title,
        value: Number(draft.value) || 0,
        thumb: draft.photos[0] ?? null,
        draftId: draft.draftId,
      }) === true;
    } catch {
      sheet = false;
    }
    if (!sheet) {
      notify(await settle(created.id, created.verifyJobId ?? null, draft));
      return;
    }
    /* The chip can only turn "Live", and only on the ROW's word (settle). At
       or over the high-value line it never does, so there is nothing to wait
       for: the app does not poll there either (create.tsx,
       showsHighValueNotice). */
    if (Number(draft.value) >= HIGH_VALUE_CENTS_THRESHOLD) return;
    if ((await settle(created.id, created.verifyJobId ?? null, draft)) === LIVE_MESSAGE) {
      /* Swallowed for the same reason: the sheet is up and says "Under
         review", which is never false; a chip that could not turn is a chip
         that stays, not a failed publish. */
      try {
        (win as PublishWindow).__gbzPublishedLive?.(draft.draftId);
      } catch {
        /* the chip stays "Under review" */
      }
    }
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;

    /* THE SUBCATEGORY GATE, at step 1 rather than step 5.
     *
     * His step-1 check omits wiz.sub in BOTH places that compute it — the
     * rendered `canNext` (app.js:1788) and `wizNextCheck` on every input
     * (app.js:4884) — while the listing route requires a subcategory for 8 of
     * the 9 categories. Without this, a seller picks "Fashion", never taps a
     * subcategory chip, fills four more steps, and is refused at the last
     * possible moment.
     *
     * Refused with the same sentence the publish pre-flight uses, so the seller
     * meets one rule once rather than two phrasings of it. This costs them a
     * tap, not a wizard.
     *
     * 🪤 An interception rather than a `disabled` attribute: wizNextCheck runs
     * on every keystroke and would re-enable a button we disabled, and a
     * subcategory tap re-renders through canNext which omits it too. Fighting
     * two writers for one attribute loses; refusing the click does not.
     */
    if (target?.closest?.(NEXT_SELECTOR)) {
      const d = (win as PublishWindow).__gbzWizardPublishDraft?.();
      if (
        d &&
        d.step === DETAILS_STEP &&
        d.isAvailable &&
        d.cat &&
        d.cat !== SUBCATEGORY_EXEMPT &&
        !d.sub?.trim()
      ) {
        event.stopPropagation();
        event.preventDefault();
        notify(`subcategory is required for category '${d.cat}'`);
      }
      return;
    }

    if (!target?.closest?.(PUBLISH_SELECTOR)) return;

    // The write guard also has this click and will suppress his fabricator. We
    // do not stop propagation: letting the guard run is the fail-safe.
    if (busy) return;

    const draft = (win as PublishWindow).__gbzWizardPublishDraft?.();
    if (!draft) return;
    if (!draft.isEdit && (draft.draftId ? draft.draftId === publishedDraftId : published)) {
      notify(UNDER_REVIEW_MESSAGE);
      return;
    }

    /* Each press owns the latch it set: the create frees the button early
       (publish, after the per-draft latch) and its poll can outlive a later
       press's save, whose latch this finally must not clear (adversarial pass
       4, card 1219177682942591). */
    const mine = ++pressSeq;
    busy = true;
    void publish(draft)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => {
        if (pressSeq === mine) busy = false;
      });
  };

  win.document.addEventListener("click", onClick, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    /* The lent uploader closes over THIS install's authFetch and cache, so it
       must not outlive the install — a torn-down publish leaving a live
       __gbzUploadWizardPhoto behind would hand the scan a stale token and an
       orphaned cache. Guarded so a re-install's newer function is never
       deleted by an older teardown arriving late. */
    const scoped = win as PublishWindow & { __gbzUploadWizardPhoto?: typeof uploadOne };
    if (scoped.__gbzUploadWizardPhoto === uploadOne) delete scoped.__gbzUploadWizardPhoto;
  };
}
